<template>
  <div>
    <div class="row">
      <div class="col-lg-12">
        <div
          id="unitContentWrapper"
          class="kt-portlet kt-portlet--height-fluid"
        >
          <div
            class="kt-portlet__head kt-portlet__head kt-portlet__head--lg kt-portlet__head--noborder kt-portlet__head--break-sm"
          >
            <div
              class="kt-portlet__head-label flex-column justify-center align-baseline pt-4"
            >
              <h3 id="unitTitle" class="kt-portlet__head-title">
                <span class="text-warning">{{ module.name }}: </span>
                {{ unit.name }}
              </h3>
              <h5 class="mt-1">
                Status: {{ isCompleted ? 'Completed' : 'Open' }}
              </h5>
            </div>
          </div>
          <div class="kt-portlet__body">
            <div class="video-wrapper mt-3">
              <v-skeleton-loader
                type="card-heading, image"
                v-if="scormLoader"
              />
              <div
                class="mx-auto embed-container"
                id="unit-content"
                :class="{
                  'embed-padding-bottom': isAVideoTypeUnit,
                  'move-outside-page': scormLoader,
                }"
              >
                <vue-friendly-iframe v-if="unitIsScorm" :src="scormSrc" />
                <div v-else v-html="contents" />
                <h4 class="text-center">Your Course is Loading...</h4>
              </div>

              <div class="text-center mt-3 mod-tag">
                <span>Tags:</span>
                <v-chip
                  class="ml-1 grey lighten-3 mt-2"
                  small
                  v-for="tag in tags"
                  v-bind:key="tag.id"
                  >{{ tag.name }}</v-chip
                >
              </div>
              <div class="text-center mt-3 w-75 p-3 description">
                <p>{{ unit.description }}</p>
              </div>
              <div class="mb-2 text-center">
                <v-btn
                  text
                  class="mr-3"
                  color="blue-grey dark-3"
                  @click="previous"
                  small
                >
                  <v-icon class="mr-2">fal fa-backward</v-icon>
                  Previous Course
                </v-btn>
                <v-btn
                  v-if="quiz === null && !unitIsScorm"
                  color="primary"
                  :disabled="isCompleted"
                  :loading="loadingMark"
                  @click="markAsCompleted"
                  text
                >
                  {{ !isCompleted ? 'Mark as Completed' : 'Completed' }}
                </v-btn>
                <v-btn
                  text
                  v-else-if="quiz !== null && !unitIsScorm"
                  color="primary"
                  :disabled="quizResult !== null && quizResultForm !== null"
                  :loading="loadingMark"
                  @click="submitQuiz"
                  >Submit</v-btn
                >
                <v-btn
                  text
                  class="ml-3"
                  color="blue-grey dark-3"
                  @click="next"
                  small
                >
                  <v-icon class="mr-2">fal fa-forward</v-icon>
                  Next Course
                </v-btn>
                <div class="text-center mt-2">
                  <v-menu>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" text small>
                        <v-icon class="mr-2">fal fa-list-ul</v-icon>
                        More Options
                      </v-btn>
                    </template>

                    <v-list>
                      <v-list-item>
                        <v-btn
                          dark
                          small
                          outlined
                          color="grey darken-3"
                          @click.stop="showShareUnit = true"
                          text
                        >
                          <v-icon class="mr-2">fal fa-share-alt</v-icon>
                          Share
                        </v-btn>
                        <share-unit
                          :unit_id="unit.id"
                          v-model="showShareUnit"
                        />
                      </v-list-item>
                      <v-list-item>
                        <v-btn
                          dark
                          small
                          outlined
                          color="grey darken-3"
                          @click.stop="showAssignUnit = true"
                          text
                        >
                          <v-icon class="mr-2">fal fa-users-cog</v-icon>
                          Assign
                        </v-btn>
                        <assign-unit
                          :unit_id="unit.id"
                          v-model="showAssignUnit"
                        />
                      </v-list-item>
                      <v-list-item>
                        <v-btn
                          dark
                          small
                          outlined
                          color="grey darken-3"
                          @click.stop="showUnitPlaylist = true"
                          text
                        >
                          <v-icon class="mr-2">fal fa-layer-plus</v-icon>
                          Add to Playlist
                        </v-btn>
                        <unit-playlist
                          :unit="unit"
                          v-model="showUnitPlaylist"
                        />
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </div>
              <v-card
                color="grey lighten-4"
                flat
                class="pa-4 text-left mt-3"
                v-if="quiz !== null && quizResult == null"
              >
                <v-card-title>Quiz</v-card-title>
                <v-card class="px-5 py-4">
                  <v-row
                    v-for="(question, index) in quiz.questions"
                    v-bind:key="question.id"
                  >
                    <v-col>
                      <h4>{{ question.value }}</h4>
                      <div
                        v-for="answer in question.answers"
                        v-bind:key="answer.id"
                      >
                        <div class="form-check mb-2">
                          <input
                            class="form-check-input"
                            :type="question.is_multiple ? 'checkbox' : 'radio'"
                            :name="index"
                            :value="answer.id"
                            @click="selectAnswer(answer.id, question.id)"
                            :id="answer.id"
                          />
                          <label class="form-check-label" :for="answer.id">{{
                            answer.value
                          }}</label>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-card>
              <v-card
                color="grey lighten-4"
                flat
                class="pa-4 text-left mt-3"
                v-else-if="quizResult"
              >
                <v-card-title>Quiz</v-card-title>
                <v-card class="px-5 py-4">
                  <v-row
                    v-for="(question, index) in quizResultForm"
                    v-bind:key="question.id"
                  >
                    <v-col>
                      <h4>{{ question.value }}</h4>
                      <div
                        v-for="(answer, index) in question.answers"
                        v-bind:key="answer.id"
                      >
                        <div class="form-check m-2">
                          <input
                            class="form-check-input"
                            :type="question.is_multiple ? 'checkbox' : 'radio'"
                            :name="index"
                            :checked="
                              isUserAnswer(question.user_answer, answer.id)
                            "
                            :value="answer.id"
                            disabled
                            @click="selectAnswer(answer.id, question.id)"
                            :id="answer.id"
                          />
                          <label
                            class="form-check-label"
                            :for="answer.id"
                            :class="
                              answer.is_correct ? 'text-success' : 'text-danger'
                            "
                            >{{ answer.value }}</label
                          >
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row
                    >TOTAL SCORE: {{ quizResult.total_score }} /
                    {{ quizResult.total_number_of_questions }}</v-row
                  >
                </v-card>
              </v-card>

              <div class="row">
                <div class="col-lg-8">
                  <notes :unitId="$route.params.id"></notes>
                </div>
                <div class="col-lg-4">
                  <!-- start of progress bar -->
                  <div class="kt-portlet">
                    <div
                      class="kt-portlet__head kt-portlet__head kt-portlet__head--lg kt-portlet__head--noborder kt-portlet__head--break-sm"
                    >
                      <div
                        class="kt-portlet__head-label flex-column justify-center align-baseline"
                      >
                        <h3 class="kt-portlet__head-title">Unit Progress</h3>
                      </div>
                    </div>
                    <div class="kt-portlet__body pt-0">
                      <div
                        class="d-flex align-items-center mb-4"
                        v-for="(item, index) in unitProgressRender"
                        :key="index"
                      >
                        <div class="mr-5">
                          <i
                            style="font-size: 1.7rem; color: #ffb822"
                            :class="
                              item.checked ? checkedClass : 'fal fa-circle'
                            "
                          />
                        </div>
                        <div class="d-flex flex-column font-weight-bold">
                          <a
                            href="javascript:"
                            class="text-dark text-hover-primary mb-1 font-size-lg"
                          >
                            {{ item.title }}
                          </a>
                          <span class="text-muted">
                            {{ item.description }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="kt-portlet">
                    <div
                      class="kt-portlet__head kt-portlet__head kt-portlet__head--lg kt-portlet__head--noborder kt-portlet__head--break-sm"
                    >
                      <div
                        class="kt-portlet__head-label flex-column justify-center align-baseline"
                      >
                        <h3 class="kt-portlet__head-title">Course Progress</h3>
                      </div>
                    </div>
                    <div class="kt-portlet__body">
                      <v-progress-linear
                        color="#f9b418"
                        height="30"
                        :value="module.build.progress"
                      >
                        <template v-slot="{ value }">
                          <strong
                            >{{ Math.ceil(module.build.progress) }}%</strong
                          >
                        </template>
                      </v-progress-linear>
                      <p class="subtitle-2">
                        This progress bar updates whenever you complete a unit
                        in this module.
                      </p>
                    </div>
                  </div>
                  <related-units :unitId="$route.params.id"></related-units>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  filter as _filter,
  findIndex as _findIndex,
  find as _find,
  forOwn as _forOwn,
  isEmpty as _isEmpty,
  some as _some,
} from 'lodash-es';
import ShareUnit from './dialog-box/ShareUnit.vue';
import AssignUnit from './dialog-box/AssignUnit.vue';
import UnitPlaylist from './dialog-box/UnitPlaylist.vue';
import RelatedUnits from './UnitPage/RelatedUnits.vue';
import Notes from './UnitPage/Notes.vue';

export default {
  name: 'UnitPage',

  components: {
    Notes,
    RelatedUnits,
    ShareUnit,
    AssignUnit,
    UnitPlaylist,
  },

  data: () => ({
    loadingMark: false,
    isFetching: false,
    showShareUnit: false,
    showAssignUnit: false,
    showUnitPlaylist: false,
    quizDialog: false,
    modprog: 78,
    answers: [],
    player: null,
    vPlayer: null,
    quizAnswered: false,
    unitProgress: [
      {
        type: 'videoWatched',
        title: 'Watched the Video',
        description: 'you will be scored once you played the video',
        checked: false,
        hide: false,
      },
      {
        type: 'videoCompleted',
        title: 'Finished the Video',
        description:
          'you will be scored once you finished watching the whole video',
        checked: false,
        hide: false,
      },
      {
        type: 'quizCompleted',
        title: 'Answered the Quiz',
        description: 'you will be scored once you answered the provided quiz',
        checked: false,
        hide: false,
      },
      {
        type: 'completedUnit',
        title: 'Completed the Unit',
        description: 'you will be scored once you mark the unit as completed',
        checked: false,
        hide: true,
      },
    ],
    isAVideoTypeUnit: true,
    unitIsScorm: true,
    scormSrc: null,
    scormLoader: false,
  }),

  computed: {
    unit() {
      return this.$store.state.clientUnit.selectedItem.unit;
    },
    tags() {
      return this.$store.state.clientUnit.selectedItem.unit.tags;
    },
    module() {
      return this.$store.state.clientUnit.selectedItem.unit.module;
    },
    contents() {
      if (
        this.$store.state.clientUnit.selectedItem.unit.content.includes('scorm')
      ) {
        return '';
      }

      return this.$store.state.clientUnit.selectedItem.unit.content;
    },
    quiz() {
      return this.$store.state.clientUnit.selectedItem.unit.quiz;
    },
    quizResult() {
      return this.$store.state.clientUnit.quizResult;
    },
    quizResultForm() {
      return this.quizResult ? JSON.parse(this.quizResult.quiz_form) : null;
    },
    userId() {
      return this.$auth.user().id;
    },
    isCompleted() {
      const unitCompleted = _find(this.unitProgress, { type: 'completedUnit' });
      if (unitCompleted.checked) {
        return true;
      }

      if (
        this.$store.state.relatedUnits.playlist.units === undefined
        || this.$store.state.relatedUnits.playlist.units.length < 1
      ) {
        return false;
      }

      if (this.$store.state.relatedUnits.type === 'playlist') {
        const index = _findIndex(
          this.$store.state.relatedUnits.playlist.units,
          {
            id: this.unit.id,
          },
        );

        return this.$store.state.relatedUnits.playlist.units[index]
          .is_completed;
      }
      return (
        this.$store.state.clientUnit.selectedItem.is_completed === 1
        || this.$store.state.clientUnit.selectedItem.is_completed
      );
    },
    unitProgressRender() {
      const filtered = _filter(this.unitProgress, { hide: false });

      return _forOwn(filtered);
    },
    played: {
      get() {
        const watchedIndex = 0;
        return this.unitProgress[watchedIndex].checked;
      },
      set() {
        this.checkUnitProgress('videoWatched');
      },
    },
    checkedClass() {
      return 'fa fa-check-circle animate__animated animate__tada';
    },
  },

  created() {
    // this.maybeTrackSeachAction();
    this.fetchUnit();
    this.isPlaylistOrSingleUnit();
  },

  beforeDestroy() {
    this.checkProgress();
  },

  methods: {
    checkProgress() {
      const unitId = this.$route.params.id;
      const userId = this.$auth.user().id;
      this.$http.post('get-learner-progress', {
        unit_id: unitId,
        user_id: userId,
      });
    },
    maybeTrackSeachAction() {
      if (this.$route.query.track && this.$route.query.track == 1) {
        const modelId = this.$route.params.id;
        const modelType = 'App\\Models\\Unit';
        this.$http.post('search-analytics', {
          model_type: modelType,
          model_id: modelId,
        });
      }
    },
    isQuizAlreadyAnswered() {
      this.axios
        .get(`/client/lms/quiz/${this.quiz.id}`, {
          params: {
            unit_id: this.unit.id,
          },
        })
        .then(({ data }) => {
          this.$store.commit('clientUnit/setQuizResult', data.data);

          if (data.data) {
            this.checkUnitProgress('quizCompleted');
          }
        })
        .catch((error) => {
          this.$store.commit('clientUnit/setQuizResult', null);
        });
    },

    selectAnswer(answerId, questionId) {
      const answer = {
        questionId,
        answerId,
      };

      const existingIndex = _findIndex(this.answers, answer);

      if (existingIndex === -1) {
        return this.answers.push(answer);
      }

      return this.answers.splice(existingIndex, 1);
    },

    isUserAnswer(userAnswersId, currentAnswerId) {
      return userAnswersId.includes(currentAnswerId);
    },

    submitQuiz() {
      if (_isEmpty(this.answers)) {
        return this.$swal({
          title: 'Unit Completion Failed!',
          text: 'Please answer the quiz first!',
          icon: 'warning',
        });
      }

      this.loadingMark = true;
      this.axios
        .post('client/lms/quiz', {
          answers: this.answers,
          unit_id: this.$store.state.markAsCompleted.current_unit.id,
        })
        .then(() => {
          this.quizAnswered = true;
          this.isQuizAlreadyAnswered();
          this.markAsCompleted();
        })
        .finally(() => {
          this.loadingMark = false;
          this.quizAnswered = true;
        });
    },

    isPlaylistOrSingleUnit() {
      const { playlistId } = this.$route.params;

      if (playlistId == undefined) {
        return this.determinePlaylistType(playlistId);
      }

      if (
        this.$store.state.markAsCompleted.playlist_id
        || (Array.isArray(this.$store.state.markAsCompleted.type)
          && this.$store.state.markAsCompleted.playlist_id)
      ) {
        this.loadingMark = true;
        return this.determinePlaylistType(playlistId);
      }
      if (!this.$store.state.markAsCompleted.current_unit.id) {
        return this.$store.commit(
          'markAsCompleted/setCurrentUnitId',
          this.$route.params.id,
        );
      }
    },

    determinePlaylistType(playlistId = null) {
      if (playlistId === null || playlistId === undefined) {
        return;
      }
      return this.$store
        .dispatch('markAsCompleted/determinePlaylistType', {
          playlistId: !_isEmpty(this.$store.state.markAsCompleted.playlist_id)
            ? this.$store.state.markAsCompleted.playlist_id
            : playlistId,
          type: this.$store.state.markAsCompleted.type,
          unitId: this.$store.state.markAsCompleted.current_unit.id,
        })
        .then(({ data }) => {
          this.loadingMark = false;
          this.$store.commit('markAsCompleted/setType', data);
          this.$store.commit('relatedUnits/setType', 'playlist');
        });
    },

    markAsCompleted() {
      this.loadingMark = true;
      this.$store
        .dispatch('markAsCompleted/markAsCompleted', {
          playlistId: this.$route.params.playlistId,
          type: this.$store.state.markAsCompleted.type,
          unitId: this.$store.state.markAsCompleted.current_unit.id,
        })
        .then(({ data }) => {
          this.$store.commit(
            'relatedUnits/setIsCompletedToTrue',
            this.$store.state.markAsCompleted.current_unit.id,
          );
          this.$store.commit('markAsCompleted/setIsCompleted', true);
          this.$store.commit('clientUnit/setCompleted', true);
          this.loadingMark = false;
          this.checkUnitProgress('quizCompleted');
          this.checkUnitProgress('completedUnit');
        });
    },
    loadVimeoPlayerSettings(unitId = null) {
      const player = document.querySelector('#unit-content > iframe');

      this.vPlayer = new Vimeo.Player(player);

      this.vPlayer.on('play', () => {
        this.processPlayedVideo(unitId);
      });

      this.vPlayer.on('ended', () => {
        this.markUnitAsFinishedTheVideo(unitId);
      });
    },
    processPlayedVideo(unitId) {
      const { type } = this.$store.state.relatedUnits;

      if (this.played) {
        return;
      }

      this.$http
        .post('/mark-user-unit-video-played', {
          unit_id: unitId,
          type,
          playlistId: this.$store.state.markAsCompleted.playlist_id,
        })
        .then(() => {
          const completedIndex = 0;

          this.played = true;
          this.unitProgress[completedIndex].checked = true;
        });
    },
    fetchUnit() {
      this.isFetching = true;
      this.$store
        .dispatch('clientUnit/getClientUnit', this.$route.params.id)
        .then(({ data }) => {
          if (data.unit.content.includes('scorm')) {
            const src = data.unit.content.match(/(?:src=").*?(?=[\?"])/g);
            const url = src[0].replace('src="', '');
            this.scormSrc = url;
            this.scormLoader = true;

            setTimeout(() => {
              this.scormLoader = false;
            }, 1000);
          }

          this.$store.commit('markAsCompleted/setCurrentUnitId', data.unit.id);
          this.$store.commit('clientUnit/setSelectedItem', data);
          this.isFetching = false;
          this.$store.commit(
            'markAsCompleted/setIsCompleted',
            !!data.is_completed,
          );

          if (data.is_played === 1) {
            this.checkUnitProgress('videoWatched');
          }

          if (data.is_completed === 1) {
            this.checkUnitProgress('completedUnit');
          }

          if (data.is_finished_watching === 1) {
            this.checkUnitProgress('videoCompleted');
          }

          if (data.unit.quiz_id) {
            this.isQuizAlreadyAnswered();
          } else {
            this.$store.commit('clientUnit/setQuizResult', null);
            this.hideUnitProgress('quizCompleted');
          }

          // if the Unit is not a video type
          // then just show the "Completed the Unit" task
          const videoKeywords = ['vimeo', 'scorm'];
          if (
            !_some(videoKeywords, item => data.unit.content.includes(item))
          ) {
            this.isAVideoTypeUnit = false;
            this.hideVideoUnitProgressTasks();
          }
        })
        .catch(() => {
          this.$router.push({
            name: 'PageNotFound',
          });
        })
        .finally(() => {
          this.loadVimeoPlayerSettings(
            this.$store.state.markAsCompleted.current_unit.id,
          );
        });
    },
    addToFavoriteDialog() {
      this.$swal({
        text: 'Successfully added to Favorite list.',
        icon: 'success',
      });
    },
    next() {
      if (this.$route.params.playlistId) {
        return this.navigateAroundPlaylist('next');
      }
      return this.$http
        .post('client/lms/related-units/', {
          unitId: this.$route.params.id,
          type: 'single',
          action: 'next',
        })
        .then(({ data }) => {
          if (data !== null) {
            return this.$router.push({
              name: 'UnitPage',
              params: {
                id: data,
              },
            });
          }

          return this.$notify('warning', 'This is the last unit in the list.');
        });
    },
    previous() {
      const { type } = this.$store.state.relatedUnits;
      if (type === 'playlist') {
        return this.navigateAroundPlaylist('back');
      }
      return this.$http
        .post('client/lms/related-units/', {
          unitId: this.$route.params.id,
          type: 'single',
          action: 'prev',
        })
        .then(({ data }) => {
          if (data !== null) {
            return this.$router.push({
              name: 'UnitPage',
              params: {
                id: data,
              },
            });
          }

          return this.$notify('warning', 'This is the first unit in the list.');
        });
    },
    navigateAroundPlaylist(type) {
      if (type === 'back') {
        const prevId = this.$store.getters[
          'relatedUnits/getPrevUnitInPlaylist'
        ](this.$route.params.id);

        if (Number.isInteger(prevId)) {
          return this.$router.push({
            name: 'UnitPage',
            params: {
              id: prevId,
              playlistId: this.$route.params.playlistId,
            },
          });
        }

        return this.$notify('warning', 'This is the first unit in the list.');
      }

      const nextId = this.$store.getters['relatedUnits/getNextUnitInPlaylist'](
        this.$route.params.id,
      );

      if (Number.isInteger(nextId)) {
        return this.$router.push({
          name: 'UnitPage',
          params: {
            id: nextId,
            playlistId: this.$route.params.playlistId,
          },
        });
      }

      return this.$notify('warning', 'This is the last unit in the list.');
    },
    checkUnitProgress(type) {
      const index = _findIndex(this.unitProgress, { type });

      this.unitProgress[index].checked = true;
    },
    hideUnitProgress(type) {
      const index = _findIndex(this.unitProgress, { type });

      this.unitProgress[index].hide = true;
    },
    hideVideoUnitProgressTasks() {
      const tasks = _forOwn(this.unitProgress);
      const quizCompletedTask = _find(this.unitProgress, {
        type: 'quizCompleted',
      });

      tasks.forEach((item, index) => {
        let value = item.type !== 'completedUnit';

        // if quiz exist in this unit, skip hide of quiz
        if (item.type === 'quizCompleted' && !item.hide) {
          value = false;
        }

        // if item is a "completedUnit" and
        // quiz exist in this unit, hide this item
        if (item.type === 'completedUnit' && !quizCompletedTask.hide) {
          value = true;
        }

        this.unitProgress[index].hide = value;
      });
    },
    markUnitAsFinishedTheVideo(unitId) {
      const { type } = this.$store.state.relatedUnits;

      this.$http
        .post('/client/lms/unit/finished/watching', {
          unit_id: unitId,
          type,
          playlistId: this.$store.state.markAsCompleted.playlist_id,
        })
        .then(() => {
          this.checkUnitProgress('videoCompleted');
        });
    },
  },

};
</script>

<style lang="stylus" scoped>
.editor, .output
  height 10rem

.description
  margin auto

/deep/ .kt-portlet__head-title
  font-size 1.5rem!important

#unitTitle
  font-size 1.8rem!important

.embed-container
  position relative
  overflow hidden
  max-width 100%
  min-height 0

.embed-container >>> iframe,
.embed-container >>> object,
.embed-container >>> embed
  position absolute
  top 0
  left 0
  width 100%
  height 100%
  border none

.embed-container.embed-padding-bottom
  padding-bottom 56.25%

.embedHeight
  height 100%

.move-outside-page
  position: absolute
  top: -600px
</style>
